"use client";

import { Clock, Globe, TriangleAlert } from "lucide-react";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { CardNavPending } from "@/components/feed/card-nav-pending";
import { CompanyLogo } from "@/components/feed/company-logo";
import {
	dispatchFeedApplyClicked,
	FeedAhaSignupAsk,
} from "@/components/feed/feed-aha-signup-ask";
import { useViewportImpression } from "@/components/feed/feed-impression-tracker";
import {
	LandSignalPill,
	TIER_LABEL_KEYS,
} from "@/components/feed/land-signal-pill";
import { SaveJobButton } from "@/components/feed/save-job-button";
import { AiApplyBadge } from "@/components/jobs/ai-apply-badge";
import { ApplyEmailHandoff } from "@/components/jobs/apply-email-handoff";
import { RelativeTime } from "@/components/ui/relative-time";
import {
	type ApplyUserState,
	type BadgeMeta,
	deriveEmployerTier,
	hoursSince,
	type JobApplyOutboundProps,
} from "@/lib/analytics/events";
import type { SlateContext } from "@/lib/analytics/feed-slate";
import { useAnalytics } from "@/lib/analytics/hooks";
import { buildJobRankContext } from "@/lib/analytics/rank-context";
import {
	FEED_BADGE_ROW_FLAG,
	type FeedBadgeRowArm,
	resolveFeedBadgeRowArm,
} from "@/lib/experiments/feed-badge-row";
import { gateCtaValueLabel } from "@/lib/experiments/gate-cta-value-prop";
import { DESKTOP_CHIP_LIMIT, MOBILE_CHIP_LIMIT } from "@/lib/feed/row-shape";
import type { Locale } from "@/lib/i18n/config";
import type { Dictionary } from "@/lib/i18n/get-dictionary";
import { countryFlag } from "@/lib/jobs/country-flag";
import {
	classifyFitBlocker,
	FIT_BLOCKER_LABEL_KEY,
} from "@/lib/jobs/fit-blocker";
import { jobLocationDisplay } from "@/lib/jobs/location-display";
import { publicJobId } from "@/lib/jobs/public-id";
import { sourceDisplayName } from "@/lib/jobs/source-label";
import { getDaysUntilExpiry } from "@/lib/jobs/urgency";
import {
	isFirstMoverGem,
	isHiddenGem,
	type LandSignal,
} from "@/lib/personalization/land-signal";
import { formatMatchReason } from "@/lib/personalization/match-reason";
import { isLocalJob } from "@/lib/personalization/rank-policy";
import type { ScoreBreakdown } from "@/lib/personalization/score";
import { formatPayRange, slugify } from "@/lib/utils/format";

const SENIORITY_LABEL_KEY = {
	entry: "seniorityEntry",
	mid: "seniorityMid",
	senior: "senioritySenior",
	lead: "seniorityLead",
} as const;

/** Maps `job_summaries.seniority` to its i18n label. Other enrichment values
 * ("unspecified", "unknown") and null render nothing (H2, job-ux-spec). */
function seniorityLabel(
	v: string | null | undefined,
	t?: Dictionary,
): string | null {
	if (!v || !(v in SENIORITY_LABEL_KEY)) return null;
	return (
		t?.[SENIORITY_LABEL_KEY[v as keyof typeof SENIORITY_LABEL_KEY]] ?? null
	);
}

export type JobRowProps = {
	job: {
		id: string;
		title: string;
		company: string | null;
		companyDomain?: string | null;
		companyLogoR2Key?: string | null;
		/** Confidence (0–1) behind `companyLogoR2Key` — drives the CompanyLogo
		 * tier ladder (a low-confidence cached favicon is demoted below
		 * logo.dev). Undefined/null is treated as NOT high-confidence. */
		companyLogoConfidence?: number | null;
		remoteType: string | null;
		/** Stored `jobs.geo_fit` — the employer-eligibility verdict. Required for
		 * the location label to say "Open worldwide": `remoteType` alone is a
		 * shape, not a promise about who may apply (2026-09-13 user report).
		 * Optional because a gated/preview response may omit it; absent is read
		 * as "unknown", which states the gap instead of claiming openness. */
		geoFit?: string | null;
		/** Stored `jobs.applicant_region` (US / EU / APAC / …) — the source's
		 * structured region lock, rendered as "Remote · European Union". */
		applicantRegion?: string | null;
		/** ISO 3166-1 alpha-2 of the COMPANY's location, for the flag beside the
		 * location line. Display only — never eligibility (a US-headquartered
		 * company hiring worldwide is good inventory for an Indonesian seeker).
		 * Distinct from `jobs.country`, which marks CPC partner inventory. */
		hqCountry?: string | null;
		jobType: string | null;
		payMin: number | null;
		payMax: number | null;
		payCurrency: string | null;
		payPeriod: string | null;
		/** Research-grounded pay ESTIMATE (never employer-stated) — rendered ONLY
		 * as a fallback when payMin/payMax are both null, and ALWAYS with an
		 * "Est." qualifier so it's never mistaken for a confirmed figure. See
		 * `estPayRange` below. Optional: gated/preview server responses omit it. */
		payEstMin?: number | null;
		payEstMax?: number | null;
		payEstCurrency?: string | null;
		payEstPeriod?: string | null;
		location: string | null;
		isPromoted?: boolean | null;
		postedAt: Date | null;
		/** Drives the "closes in Xd" urgency chip. Null/past renders nothing —
		 * see `getDaysUntilExpiry` (src/lib/jobs/urgency.ts). Written at ingest
		 * (either an explicit source signal or a computed default window); see
		 * `resolveExpiresAt` in src/lib/ingestion/cron-shell-core.ts. */
		expiresAt?: Date | null;
		/** `jobs.expires_at_source` — "source" when the employer supplied a real
		 * deadline, "inferred" when the date is our own default window. 98.7% of
		 * live jobs are "inferred" (prod D1, 2026-09-07), so the urgency chip's
		 * presence and the truth of its claim are two different facts and are
		 * reported to analytics as two different fields. */
		expiresAtSource?: string | null;
		/** `slug` = `sources.slug`, reported as the `source_id` rank-context
		 * prop. Optional: not every feed query selects it. */
		source: { name: string; slug?: string | null } | null;
		/** `job_summaries.seniority` — entry|mid|senior|lead; other enrichment values
		 * ("unspecified", "unknown") and null render nothing. */
		seniority?: string | null;
		summary: { text: string | null } | null;
		hook?: string | null;
		hasGatedSalary?: boolean;
		isPreview?: boolean;
		upgradeMessage?: string;
		/** Personalization score 0-100; undefined when feed is chronological. */
		relevanceScore?: number;
		/** Full per-dimension breakdown. The cold-start path attaches an
		 *  all-zero `nullBreakdown` with `isPersonalizedFit: false`; a real
		 *  ranked row carries live category/skills/seniority/jobType/remoteType
		 *  scores. Rendered into a 1-line "why this matches" reason via
		 *  `formatMatchReason` on personalized rows only. */
		relevanceBreakdown?: ScoreBreakdown;
		chronologicalRank?: number;
		personalizedRank?: number;
		/** Ranking variant (diversity-rerank A/B arm) — tagged onto impression /
		 * click events to read CTR per arm. */
		algoVersion?: string;
		/** `feed-rank-policy` arm, slot propensity and exploration mark, set
		 * by the ranked feed paths (src/lib/personalization/rank-policy.ts). */
		rankPolicy?: string;
		propensity?: number;
		explored?: boolean;
		/** Interleaved arms only: contributing team + `<a>_vs_<b>` pair
		 * (src/lib/personalization/interleave.ts). */
		team?: "a" | "b";
		interleavePair?: string;
		/** Page 1 of an arm with a Hunt entry card: its visual slot. */
		huntCardSlot?: number;
		/** Autopilot weights version that assigned `rankPolicy`. */
		rankPolicyWeightsVersion?: string;
		/** `ltr` arms: learned-ranker version (null = base order served). */
		rankModelVersion?: string | null;
		categoryId?: string | null;
		categoryIsRich?: boolean | null;
		categorySlugs?: string[];
		isAiEnriched?: boolean | null;
		/** LLM-derived generic Indonesia suitability (0–1) from the job's
		 * id-locale summary. Used server-side by the "Indonesia-friendly only"
		 * feed filter; NOT rendered as a positive row badge (see fit-blocker.ts). */
		indonesiaFitScore?: number | null;
		/** LLM prose explaining the fit score. Classified into a concrete
		 * eligibility-blocker chip (US-only / on-site / …) for the row. */
		fitReason?: string | null;
		/** Land Signal — per-user, per-job hireability indicator. Rendered as a
		 * "fit" pill ONLY when {@link isPersonalizedFit} is true (the row was
		 * ranked against the viewer's profile); otherwise hidden so a generic
		 * job-only score isn't misrepresented as a personal match. */
		landSignal?: LandSignal;
		/** True only when `landSignal` reflects a real profile-personalized
		 * ranking (authenticated user with signals). Undefined/false for
		 * anonymous cold-start rows — the pill is suppressed in that case. */
		isPersonalizedFit?: boolean;
		/** Real outbound `/out` apply-click count (`jobs.apply_clicks`,
		 * `NOT NULL DEFAULT 0`) — the same demand signal now feeding the Land
		 * Signal competition axis (T9). Drives the numeric "Be among the first
		 * N to apply" copy on the hidden-gem badge below; falls back to the
		 * qualitative "Low competition" text when absent (e.g. rows from a
		 * feed path that never attached it). */
		applyClicks?: number | null;
		/** Raw "x/y" overlap between the user's declared skills and this job's
		 * core (required) skills — computed once in the feed orchestrator
		 * (T10, `computeSkillsMatchDetail`). Only present on personalized rows;
		 * null when the job lists no core skills or the user has no declared
		 * skill tokens to compare. */
		skillsMatch?: { matched: number; total: number } | null;
		/** Linked employer_orders.id when the job was published via a self-serve
		 * submission. NULL/undefined for ingested jobs. Threaded into the apply
		 * event so the NSM can attribute QA to paid vs organic supply. */
		employerOrderId?: string | null;
		/** Raw employer_orders.product_key (e.g. `employer_pilot_free`,
		 * `employer_standard`). Mapped to the analytics `employer_tier` via
		 * `deriveEmployerTier()`. NULL for organic jobs. */
		employerOrderProductKey?: string | null;
		/** True when the apply destination lands on an ATS the extension autofills
		 * in ~1 click. Drives the flag-gated "AI-powered apply" badge. Derived
		 * server-side from the apply-URL columns (never the URL itself). */
		atsSupported?: boolean;
		/** True for any job with an `apply_email` — extracted from the posting OR
		 * derived from the company domain. Drives the apply-by-email handoff CTA
		 * on the row, mirroring the job-detail gate (`isEmailApply`). Derived
		 * server-side; the raw address is never sent to the client. */
		showEmailHandoffCta?: boolean;
	};
	/** 1-based absolute position in the rendered list (reported as `position`). */
	index: number;
	/** Which list this row is rendered in — the `list_surface` analytics prop.
	 * Defaults to `feed`. */
	listSurface?: string;
	/** Page-level ranking variant (see `feedAlgoVersion`). A row's own
	 * `job.algoVersion` wins; this fills the default SQL feed path, whose rows
	 * carry none — the gap behind 40% of clicks lacking `algo_version`. */
	algoVersion?: string;
	/** Browser-generated id of the served feed page (src/lib/analytics/
	 * feed-slate.ts), logged on impression/click/apply. Feed surface only. */
	slateId?: string;
	isAuthenticated: boolean;
	isPreview?: boolean;
	t?: Dictionary;
	locale?: string;
	/** Caller-supplied user_state. Falls back to guest/free from isAuthenticated. */
	userState?: ApplyUserState;
	/** 1-indexed page in the feed; threaded so apply events can attribute pagination. */
	feedPageNumber?: number;
	/** True when the viewer's plan allows saving jobs. When false an authed user
	 * sees the upgrade modal; an unauthed (guest) viewer is redirected to login
	 * — pass `false` for guests, the redirect is handled inside SaveJobButton. */
	canSaveJobs?: boolean;
	/** Caller-supplied set of saved job IDs for the viewer; drives the filled
	 * bookmark on rows the user already saved. Omit for guests (no save state). */
	savedJobIds?: Set<string>;
	/**
	 * Server-derived current path, used ONLY as the post-signup callback
	 * target on a gated card's CTA. Deliberately a plain prop (not
	 * `usePathname()`) — the previous `usePathname()`/`useSearchParams()`
	 * calls inside this client component forced React to bail the entire
	 * prerendered subtree to client-side rendering wherever the parent list
	 * had no `<Suspense>` boundary of its own (e.g. company pages, which
	 * shipped an empty CSR shell with zero SSR job links). Threading the
	 * already-known path/query as props keeps the row prerender-safe
	 * everywhere. Falls back to `/${locale}` when omitted.
	 */
	currentPath?: string;
	/**
	 * Server-derived `?q=` search query, threaded the same way as
	 * `currentPath` above — see that doc comment. Only the `/jobs` search UI
	 * ever sets this (that route is `force-dynamic`, unaffected by the
	 * bailout); every other caller omits it and gets `null` (not a search
	 * result), matching the previous behavior on those routes.
	 */
	searchQuery?: string | null;
};

/**
 * The row date renders twice: the server (and the edge-cached HTML) prints an
 * absolute date ("Nov 1, 2026", up to ~77px at text-xs) and `<RelativeTime>`
 * swaps in "17h ago" (~46px) after mount. The right-hand column is
 * `shrink-0`, so the narrower label pulled it right and reflowed every row:
 * a 0.03-0.07 layout shift on every feed load, the biggest single source in
 * the prod trace of 2026-11-02 (`?page=` CLS p75 0.20-0.63 field, 0.15 in the
 * smoke spec). A min-width that holds both labels keeps the box fixed; widest
 * measured label is "59 dtk yang lalu" at 90.3px, under 5.75rem (92px).
 */
const ROW_DATE_CLASS = "inline-block min-w-[5.75rem] text-right";

export function JobRow({
	job,
	index,
	listSurface = "feed",
	algoVersion: pageAlgoVersion,
	slateId,
	isAuthenticated = false,
	isPreview = false,
	t,
	locale,
	userState,
	feedPageNumber,
	canSaveJobs,
	savedJobIds,
	currentPath,
	searchQuery: searchQueryProp,
}: JobRowProps) {
	const { trackJobClick, trackApplyClick, posthog } = useAnalytics();

	// `feed-badge-row` arm. Null until PostHog's /decide response lands, and
	// null resolves to `control` — see resolveFeedBadgeRowArm for why the
	// safe-off state has to be control rather than the treatment. The
	// impression below deliberately waits for this to be non-null so every
	// captured event carries PostHog's `$feature/feed-badge-row` tag; an
	// impression fired before flags load is untagged and silently drops out of
	// the analysis, which biases the result toward fast-connection sessions.
	const [badgeRowArm, setBadgeRowArm] = useState<FeedBadgeRowArm | null>(null);
	useEffect(() => {
		if (!posthog) return;
		const read = () =>
			setBadgeRowArm(
				resolveFeedBadgeRowArm(posthog.getFeatureFlag?.(FEED_BADGE_ROW_FLAG)),
			);
		read();
		// Re-read when /decide resolves after mount; returns an unsubscribe.
		return posthog.onFeatureFlags?.(read);
	}, [posthog]);
	const isRowV2 = badgeRowArm === "v2";

	const searchQuery = searchQueryProp?.trim() || null;
	const searchMeta = {
		from_search: searchQuery !== null,
		search_query: searchQuery,
	};
	// Truthful personalization flag. The old proxy (`relevanceScore != null`)
	// was defeated by the cold-start path, which sets `relevanceScore` to the
	// deterministic landSignal score — so anonymous visitors registered as
	// "personalized" and saw a fake "Strong fit" pill. Gate on the explicit
	// flag the feed layer sets only when it actually ranked against a profile.
	const isPersonalized = job.isPersonalizedFit === true;
	const categoryMeta = { category_slugs: job.categorySlugs ?? [] };
	// Same six keys on impression, click and apply (PostHog audit 2026-10-21).
	const rankContext = buildJobRankContext({
		position: index,
		listSurface,
		score: job.relevanceScore,
		algoVersion: job.algoVersion ?? pageAlgoVersion,
		sourceSlug: job.source?.slug,
		geoFit: job.geoFit,
	});
	const resolvedAlgoVersion = rankContext.algo_version ?? undefined;
	const isLocal = isLocalJob(job);
	// Logged on impression, click and feed apply so a later PR can run
	// off-policy evaluation of `feed-rank-policy` arms. null = unranked path.
	const rankPolicyMeta = {
		slate_id: slateId ?? null,
		rank_policy: job.rankPolicy ?? null,
		rank_policy_weights_version: job.rankPolicyWeightsVersion ?? null,
		propensity: job.propensity ?? null,
		explored: job.explored ?? null,
		is_local: isLocal,
		// Interleaved arms: the readout credits the click/apply to this team.
		team: job.team ?? null,
		interleave_pair: job.interleavePair ?? null,
	};
	// The feed-card email handoff writes the apply receipt straight from this
	// row, so it gets the slate directly (no card click stashed one).
	const emailHandoffSlate: SlateContext | null = slateId
		? {
				slate_id: slateId,
				position: index,
				rank_policy: job.rankPolicy ?? null,
				propensity: job.propensity ?? null,
			}
		: null;
	const relevanceMeta = {
		...rankPolicyMeta,
		relevance_score: job.relevanceScore,
		is_personalized: isPersonalized,
		algo_version: resolvedAlgoVersion,
		category_pts: job.relevanceBreakdown?.category,
		skills_pts: job.relevanceBreakdown?.skills,
		seniority_pts: job.relevanceBreakdown?.seniority,
		fit_pts: job.relevanceBreakdown?.fit,
		chronological_rank: job.chronologicalRank,
		personalized_rank: job.personalizedRank,
		rank_delta:
			typeof job.chronologicalRank === "number" &&
			typeof job.personalizedRank === "number"
				? job.chronologicalRank - job.personalizedRank
				: undefined,
	};

	const payRange = formatPayRange(
		job.payMin,
		job.payMax,
		job.payCurrency,
		job.payPeriod,
	);
	// Research-grounded estimate — only rendered when the employer stated no
	// pay at all (T20). Must always carry the "Est." qualifier so a reader
	// never mistakes it for a confirmed, employer-provided figure.
	const estPayRange = payRange
		? null
		: formatPayRange(
				job.payEstMin,
				job.payEstMax,
				job.payEstCurrency,
				job.payEstPeriod,
			);
	const estPayLabel = estPayRange
		? `${t?.payEstimatePrefix || (locale === "id" ? "Estimasi" : "Est.")} ${estPayRange}`
		: null;

	// Explicit, LinkedIn-style location ("United States · Remote", "Remote · US
	// East hours") instead of the bare, capitalized remoteType. Goes through the
	// shared helper so this row claims "Open worldwide" on exactly the evidence
	// the job's own detail page and FAQ require — the stored geo_fit column,
	// which this call site used to omit entirely.
	const locationDisplay = jobLocationDisplay(job, locale);

	// Null for an unresolvable or absent code, which renders nothing at all.
	const hqFlag = countryFlag(job.hqCountry);

	const isGated = isPreview || job.isPreview;
	// Which tier of the blurb fallback chain actually rendered. Exposed as
	// `data-blurb-source` on the blurb <p> (both the mobile and desktop layout
	// render one) so e2e can assert the CONTRACT — "an AI hook, when present,
	// outranks the summary" — instead of pinning the literal AI-generated copy.
	//
	// 🔴 2026-07-19: tests/e2e/ai-enrichment.spec.ts asserted the exact seeded
	// hook string and required it on page 1 of a ranked, time-decaying feed, so
	// it rotted and turned every open PR red — including docs-only ones. Its
	// SIBLING test in the same file had already been rewritten to assert shape
	// after the same failure on 2026-06-30; the lesson just never reached this
	// one. Keep this in sync with the JSX fallback chain below.
	const blurbSource = job.hook
		? "hook"
		: job.summary?.text
			? "summary"
			: "fallback";
	// Saved state is caller-supplied (the feed page fetches the viewer's saved
	// IDs once and threads the Set through). Gated/preview rows never render the
	// save button, so the value is irrelevant there.
	const isSaved = savedJobIds?.has(job.id) ?? false;
	// Eligibility-blocker chip — null for the vast majority (the board's
	// baseline is Indonesia-friendly; we only flag the exception).
	const fitBlocker = classifyFitBlocker(job.fitReason);
	// Second-chance geo experiment (`show-ambiguous-geo-jobs`): a restricted row
	// only reaches the feed in the arm that admits them, so this is false for
	// every other viewer.
	const isGeoRestricted = job.geoFit === "restricted";
	const [geoReported, setGeoReported] = useState(false);
	// "Closes in Xd" urgency chip — null (no chip) unless the employer actually
	// gave us a deadline AND it is still in the future.
	//
	// The `expiresAtSource` check is the load-bearing half, and it ships on
	// BOTH arms of `feed-badge-row` rather than inside the experiment. On
	// 2026-09-07 this chip rendered on 5,652 of 5,652 live jobs, and 98.7% of
	// those countdowns came from `expires_at_source = 'inferred'` — a default
	// window we compute at ingest, not anything the employer told us. Showing a
	// seeker "closes in 45 days" on a posting with no deadline is a false
	// claim, and a false claim is not an A/B test. The ~1.3% of jobs carrying a
	// real deadline keep the chip, where the countdown means something.
	//
	// Freshness is not lost with it: the row already renders posting age via
	// <RelativeTime> a few elements down.
	//
	// URGENCY_HYDRATION: `getDaysUntilExpiry` reads the clock during render, and
	// the feed HTML is edge-cached — so the server count ("Closes in 57d") can be
	// a day older than the browser's first render ("56d") once a day boundary
	// passes inside the cache window. That text mismatch is React #418, which
	// throws away the server HTML for the whole subtree (reproduced 2026-10-11 on
	// a cached /en/jobs HIT; a fresh render did not throw). Both chip text nodes
	// carry `suppressHydrationWarning` so hydration keeps the server count; the
	// next client render corrects it.
	const hasEmployerDeadline = job.expiresAtSource === "source";
	const daysUntilExpiry = hasEmployerDeadline
		? getDaysUntilExpiry(job.expiresAt)
		: null;
	const urgencyLabel = (t?.jobClosesInDays || "Closes in {days}d").replace(
		"{days}",
		String(daysUntilExpiry),
	);
	// Control: ≤7 days gets the attention-grabbing tint; further out is a quiet
	// fact. v2 keeps the chip neutral in every case — DESIGN.md's One Voice
	// Rule allows exactly one tinted chip per row and v2 spends it on the fit
	// signal. The raw `orange-*` utilities here are also outside the token
	// system entirely, which is a second reason not to carry them forward.
	const urgencyClassName =
		!isRowV2 && daysUntilExpiry !== null && daysUntilExpiry <= 7
			? "border-orange-500/30 bg-orange-500/10 text-orange-700 dark:text-orange-400"
			: "border-border bg-muted/40 text-muted-foreground";

	// Same rule for the hidden-gem pill: tinted in control, quiet in v2.
	const competitionClassName = isRowV2
		? "border-border bg-muted/40 text-muted-foreground"
		: "border-primary/30 bg-primary/5 text-primary";

	// `gate_impression` for a locked card is captured once per feed render
	// (all locked cards at once) by the parent list, not here — see
	// job-feed-rows.tsx. Firing it per-card here inflated the metric 30.6x/user
	// (telemetry-vs-reality-gap 2026-07-23, W1-3).

	// Apply anchors only render when !isGated, so lock_state at the click is
	// always "unlocked". Kept explicit so a future redesign that surfaces an
	// apply button inside a gated state can flip this to "preview".
	const resolvedUserState: ApplyUserState =
		userState ?? (isAuthenticated ? "free" : "guest");
	const applyPayloadBaseWithoutBadges: Omit<
		JobApplyOutboundProps,
		keyof BadgeMeta
	> = {
		origin_surface: "feed",
		source_platform: job.source?.name ?? "unknown",
		pay_present: Boolean(job.payMin || job.payMax),
		pay_currency: job.payCurrency ?? null,
		posted_age_hours: hoursSince(job.postedAt),
		is_ai_enriched: Boolean(job.isAiEnriched),
		category_id: job.categoryId ?? null,
		category_type:
			job.categoryIsRich == null
				? null
				: job.categoryIsRich
					? "rich"
					: "normal",
		feed_page_number: feedPageNumber,
		job_position_on_page: index,
		...rankContext,
		lock_state: "unlocked",
		user_state: resolvedUserState,
		page_type: "feed",
		locale: locale ?? "unknown",
		land_signal_tier: job.landSignal?.tier ?? null,
		land_signal_score: job.landSignal?.score ?? null,
		employer_id: job.employerOrderId ?? null,
		employer_tier: deriveEmployerTier(job.employerOrderProductKey),
	};
	// Guest branch ships the value-forward copy — the gate-cta-value-prop
	// experiment ended 2026-07-14 with the variant pinned (see the module doc).
	const lockMessage = isAuthenticated
		? job.upgradeMessage || t?.upgradeToPro || "Upgrade to Pro"
		: gateCtaValueLabel(locale);

	const safeLocale = locale || "en";
	// Apply-by-email handoff primary CTA — any job carrying an apply email
	// (`showEmailHandoffCta`, computed server-side in `runJobFeed`). Signed-in
	// only, matching every other render site of this component — the action
	// itself requires an authenticated session.
	const showEmailHandoffCta =
		!isGated && isAuthenticated && job.showEmailHandoffCta === true;
	// "You send it" is load-bearing copy, not modesty: the mail leaves the
	// seeker's own mailbox, so the employer's reply comes back to them.
	const emailHandoffLabel = {
		en: "Email your CV — we write it, you send it",
		id: "Email CV-mu — kami tulis, kamu kirim",
	};
	const matchReason =
		isPersonalized && job.relevanceBreakdown
			? formatMatchReason(job.relevanceBreakdown, safeLocale as Locale)
			: null;
	// Skill-match chip (T10) — surfaces the feed orchestrator's existing
	// x/y skill-overlap count. Same personalized-only honesty gate as
	// matchReason above: a cold-start row has no user skills to compare
	// against, so it never carries a skillsMatch value.
	//
	// `matched > 0` is part of that honesty gate, not a cosmetic filter. Every
	// other chip on this row is a REASON TO APPLY; a "0/5 skills match" chip is
	// the only one that argues against the row it decorates, and it was being
	// printed on rank-1 promoted rows (observed on the production guest feed,
	// 2026-08-11), where the product is actively recommending the job. There is
	// no user action a zero can prompt — it is not a filter, not a fix, just a
	// discouragement. The absence of a chip already says "no overlap to show",
	// and says it without talking the reader out of a listing.
	const skillsMatchLabel =
		isPersonalized &&
		job.skillsMatch &&
		job.skillsMatch.total > 0 &&
		job.skillsMatch.matched > 0
			? (t?.skillsMatchLabel || "{matched}/{total} skills match")
					.replaceAll("{matched}", String(job.skillsMatch.matched))
					.replaceAll("{total}", String(job.skillsMatch.total))
			: null;
	// Competition badge (T9) — an actual numeric count ("Be among the first N
	// to apply") beats the qualitative "Low competition" text whenever the
	// underlying applyClicks count is known; falls back to the qualitative
	// copy when it isn't (defensive — isHiddenGem only fires on a known count
	// today, but a future competition signal could change that).
	// v2 takes the stricter badge predicate (competition >= 15: zero recorded
	// applicants AND posted within 7 days), which moves the "be the first to
	// apply" claim from 67% of live jobs to 14.5%. The filter, picks card and
	// job-detail sidebar keep `isHiddenGem` — see isFirstMoverGem's docblock.
	const hiddenGem = job.landSignal
		? isRowV2
			? isFirstMoverGem(job.landSignal)
			: isHiddenGem(job.landSignal)
		: false;
	const competitionBadgeLabel = hiddenGem
		? typeof job.applyClicks === "number"
			? job.applyClicks === 0
				? t?.hiddenGemFirstLabel || "No applicants via Loker Dollar yet"
				: (
						t?.hiddenGemCountTemplate || "Be among the first {n} to apply"
					).replaceAll("{n}", String(job.applyClicks + 1))
			: t?.hiddenGemLabel || "Low competition"
		: null;
	const jobSlug = `${slugify(job.title)}${job.company ? `-${slugify(job.company)}` : ""}`;
	const outboundUrl = `/${safeLocale}/out/${publicJobId(job.id)}/${jobSlug}`;
	const detailUrl = `/${safeLocale}/jobs/${jobSlug}--${publicJobId(job.id)}`;
	// `aha-signup-ask` feed trigger: the /jobs feed only, server-known guest,
	// ungated row. The component itself waits for the client session check.
	const showAhaSignupAsk =
		listSurface === "feed" && !isAuthenticated && !isGated;
	const callbackTarget = currentPath || `/${safeLocale}`;
	const gatedHref = isAuthenticated
		? `/${safeLocale}/pricing`
		: `/${safeLocale}/signup?callbackURL=${encodeURIComponent(callbackTarget)}`;
	const gatedAriaLabel = isAuthenticated
		? t?.upgradeToProAria || `Upgrade to view ${job.title}`
		: t?.signUpToViewAria || `Sign up to view ${job.title}`;
	const handleGatedClick = () => {
		posthog?.capture("gate_cta_clicked", {
			surface_id: "feed_card_locked",
			gate_type: isAuthenticated ? "upgrade" : "signup",
			trigger: "view_apply",
			job_id: job.id,
		});
	};
	// H2 (job-ux-spec): seniority was fetched but never rendered on the card —
	// the #1 fact a seeker had to open the detail page to learn.
	const seniorityLbl = seniorityLabel(job.seniority, t);
	// H3 (job-ux-spec): where a job came from was only an analytics tag,
	// never shown on the card. `sourceDisplayName` filters junk/internal
	// values (e.g. "unknown", "src_careerjet") down to null.
	const sourceLbl = sourceDisplayName(job.source?.name);
	// Plain company · location line for the mobile row. These two are facts, not
	// signals, and rendering them as chips put them in visual competition with
	// the pay and fit badges that actually drive the decision.
	const mobileMetaLine = [job.company, locationDisplay.text, seniorityLbl]
		.filter(Boolean)
		.join(" · ");

	// The mobile chip row used to render up to ELEVEN chips into one
	// undifferentiated flex-wrap at 10-11px in five colour families, wrapping to
	// 4-5 lines under the blurb. That put a row at ~250-320px on a 390px screen
	// (~1.3 jobs visible at a time) and a page at ~15,000px, against scroll
	// telemetry showing half the audience never passes ~2,500px — so a feed
	// serving 20 jobs was delivering about 8.
	//
	// Nothing is deleted: the chips are ordered by decision value and the row
	// renders at most MOBILE_CHIP_LIMIT of them, so a lower-priority chip still
	// shows on rows where the higher-priority ones are absent. What is bounded
	// is the row's height, which is what the scroll curve is actually measuring.
	const payChip = isGated
		? payRange || estPayRange || job.hasGatedSalary
			? ({ key: "pay-gated" } as const)
			: null
		: payRange
			? ({ key: "pay" } as const)
			: estPayLabel
				? ({ key: "pay-est" } as const)
				: null;

	// Exactly one personal-fit signal, never a stack of three saying the same
	// thing in three different colours.
	const fitChip = isGated
		? null
		: job.landSignal && isPersonalized
			? ({ key: "land-signal" } as const)
			: matchReason
				? ({ key: "match-reason" } as const)
				: skillsMatchLabel
					? ({ key: "skills-match" } as const)
					: null;

	// ONE SCARCITY SLOT (v2). "Closes in 3d" and "be the first to apply" are
	// both arguments to act now; stacking them doubles the urgency language
	// without doubling the information. A real employer deadline outranks a
	// low-competition inference, because one is a fact about the posting and
	// the other is a fact about our own click counter.
	const aiApplyKey =
		!isGated && job.atsSupported ? ("ai-apply" as const) : undefined;

	const scarcityKey =
		daysUntilExpiry !== null
			? ("urgency" as const)
			: !isGated && competitionBadgeLabel
				? ("competition" as const)
				: undefined;

	const mobileChipKeys = [
		payChip?.key,
		fitChip?.key,
		...(isRowV2
			? ([scarcityKey] as const)
			: ([
					daysUntilExpiry !== null ? ("urgency" as const) : undefined,
					!isGated && competitionBadgeLabel
						? ("competition" as const)
						: undefined,
				] as const)),
		aiApplyKey,
	]
		.filter((k): k is NonNullable<typeof k> => Boolean(k))
		.slice(0, MOBILE_CHIP_LIMIT);

	// Desktop renders its chips inline rather than through the keyed list, so
	// this array is what the JSX consults (via `showsDesktopChip` below) rather
	// than a mirror of it. That is the point: before, the two were separate
	// expressions that had to be kept in step by hand, which is precisely how a
	// row drifts out of its own telemetry.
	//
	// Control is the shipped behaviour: no cap, and no "exactly one fit chip"
	// rule, which is how one card could show the fit pill, the hidden-gem pill,
	// the urgency chip and the skills chip at once. v2 applies the same
	// single-fit-chip and single-scarcity-slot rules as mobile and caps the
	// result. `fit-blocker` sits outside the cap deliberately — it is a
	// protective eligibility warning, not a reason to apply.
	const desktopChipKeys: string[] = isRowV2
		? [payChip?.key, fitChip?.key, scarcityKey, aiApplyKey]
				.filter((k): k is NonNullable<typeof k> => Boolean(k))
				.slice(0, DESKTOP_CHIP_LIMIT)
		: [
				!isGated && matchReason ? ("match-reason" as const) : undefined,
				!isGated && skillsMatchLabel ? ("skills-match" as const) : undefined,
				!isGated && job.landSignal && isPersonalized
					? ("land-signal" as const)
					: undefined,
				!isGated && competitionBadgeLabel
					? ("competition" as const)
					: undefined,
				aiApplyKey,
				daysUntilExpiry !== null ? ("urgency" as const) : undefined,
				!isGated && payRange ? ("pay" as const) : undefined,
			].filter((k): k is NonNullable<typeof k> => Boolean(k));
	// Outside the cap on purpose: a protective eligibility warning should never
	// lose its slot to a promotional chip.
	if (fitBlocker) desktopChipKeys.push("fit-blocker");
	// Same reasoning: the geo warning is protective, never capped away.
	if (isGeoRestricted) desktopChipKeys.push("geo-restricted");

	const desktopChipSet = new Set<string>(desktopChipKeys);
	/** Single source of truth for "does the desktop row render this chip". Both
	 * the JSX and `badges_shown` read it, so they cannot disagree. */
	const showsDesktopChip = (key: string) => desktopChipSet.has(key);

	const badgeMeta: BadgeMeta = {
		badges_shown: desktopChipKeys,
		badges_shown_mobile: mobileChipKeys,
		fit_chip_kind: fitChip?.key ?? null,
		hidden_gem_shown: Boolean(!isGated && competitionBadgeLabel),
		urgency_shown: daysUntilExpiry !== null,
		urgency_source: job.expiresAtSource ?? null,
		skills_match_shown: Boolean(!isGated && skillsMatchLabel),
		land_signal_pill_shown: Boolean(
			!isGated && job.landSignal && isPersonalized,
		),
		badge_row_arm: badgeRowArm ?? "unresolved",
	};

	const applyPayloadBase: JobApplyOutboundProps = {
		...applyPayloadBaseWithoutBadges,
		...badgeMeta,
	};

	// Impression fires once per card, when the card has actually been on
	// screen (>=50% for >=1s — `useViewportImpression`), not on mount: firing on
	// mount counted every below-the-fold row as seen. It lives here, below the
	// chip resolution, because it reports the badges the row rendered — moving
	// it back above them would report a row that had not been decided yet.
	const articleRef = useRef<HTMLElement | null>(null);
	const inViewport = useViewportImpression(articleRef);
	const lastImpressionForId = useRef<string | null>(null);
	// biome-ignore lint/correctness/useExhaustiveDependencies: badgeMeta is a fresh object literal on every render, so listing it would re-run the effect every render for no gain — the ref guard still emits once per job.id, and every badge field derives from `job`, which is already listed.
	useEffect(() => {
		// Wait for the arm. An impression captured before PostHog's /decide
		// response lands carries no `$feature/feed-badge-row` tag and drops out
		// of the experiment's denominator, which would quietly bias the readout
		// toward whoever loads flags fastest.
		if (badgeRowArm === null) return;
		if (!inViewport) return;
		if (lastImpressionForId.current === job.id) return;
		lastImpressionForId.current = job.id;
		posthog?.capture("feed_card_impression", {
			job_id: job.id,
			slot_index: index,
			origin_surface: "feed",
			relevance_score: job.relevanceScore,
			is_personalized: isPersonalized,
			algo_version: resolvedAlgoVersion,
			position: rankContext.position,
			list_surface: rankContext.list_surface,
			category_slugs: job.categorySlugs ?? [],
			category_pts: job.relevanceBreakdown?.category,
			skills_pts: job.relevanceBreakdown?.skills,
			seniority_pts: job.relevanceBreakdown?.seniority,
			fit_pts: job.relevanceBreakdown?.fit,
			chronological_rank: job.chronologicalRank,
			personalized_rank: job.personalizedRank,
			rank_delta:
				typeof job.chronologicalRank === "number" &&
				typeof job.personalizedRank === "number"
					? job.chronologicalRank - job.personalizedRank
					: undefined,
			// Land Signal exposure — previously the pill had ZERO telemetry, so its
			// effect on clicks was unmeasurable. The apply event already carries
			// land_signal_tier/score; recording them on the impression too gives the
			// denominator → CTR-by-tier is now derivable. `land_signal_shown` mirrors
			// the pill's render gate (a real personalized-fit ranking), so a generic
			// cold-start job score isn't counted as a shown "fit" verdict.
			land_signal_tier: job.landSignal?.tier ?? null,
			land_signal_score: job.landSignal?.score ?? null,
			land_signal_source: job.landSignal?.source ?? null,
			land_signal_shown: Boolean(job.landSignal) && isPersonalized,
			// The other three badges had none of this. `land_signal_shown` above is
			// kept as-is (it predates badgeMeta and is already joined against in
			// saved queries); `land_signal_pill_shown` inside badgeMeta is the same
			// fact under the naming the other badges use.
			...badgeMeta,
			// H2 (job-ux-spec): did this impression carry a renderable seniority
			// label? Feeds the click/apply-through decision rule in PR body.
			has_seniority: Boolean(seniorityLbl),
			// H3 (job-ux-spec): did this impression carry a renderable "via
			// {source}" label? Same decision-rule shape as has_seniority above.
			has_source_label: Boolean(sourceLbl),
			...rankPolicyMeta,
		});
	}, [
		job.id,
		index,
		job.relevanceScore,
		isPersonalized,
		resolvedAlgoVersion,
		rankContext.position,
		rankContext.list_surface,
		job.categorySlugs,
		job.relevanceBreakdown?.category,
		job.relevanceBreakdown?.skills,
		job.relevanceBreakdown?.seniority,
		job.relevanceBreakdown?.fit,
		job.chronologicalRank,
		job.personalizedRank,
		job.landSignal,
		job.rankPolicy,
		job.rankPolicyWeightsVersion,
		job.propensity,
		job.explored,
		job.team,
		job.interleavePair,
		isLocal,
		slateId,
		badgeRowArm,
		inViewport,
		seniorityLbl,
		posthog,
	]);

	return (
		<article
			ref={articleRef}
			// The feed shell sets translate="no" to keep Google Translate's DOM
			// rewrite off its interactive controls; `translate` inherits, so the
			// row has to opt its own prose back in or the listings themselves
			// become untranslatable.
			translate="yes"
			data-job-id={job.id}
			data-job-position={index}
			data-local={isLocal ? "true" : "false"}
			// e2e handle: a promoted row holds its slot in the indexable-first
			// default listing (tests/e2e/feed-indexable-first.spec.ts).
			data-promoted={job.isPromoted ? "true" : "false"}
			data-rank-policy={job.rankPolicy}
			data-team={job.team}
			// `hover:bg-muted/30` was the row's ONLY affordance, and hover does not
			// exist on the phones this product is built for — every tap on the
			// product's primary object went unacknowledged until the next page
			// painted. `active:` covers the first frame; <CardNavPending> covers
			// the seconds after it.
			className={`group relative flex flex-col border-b border-border bg-background px-1 py-5 transition-colors duration-200 last:border-b-0 hover:bg-muted/30 active:bg-muted/50 sm:px-2 sm:py-6 ${
				isGated ? "cursor-pointer" : ""
			} ${
				job.isPromoted
					? "rounded-xl ring-1 ring-primary/30 bg-primary/[0.02] border-b-0 my-1 px-5 sm:px-6"
					: ""
			}`}
		>
			<div className="sm:hidden">
				<div className="mb-2 flex items-center justify-between gap-2">
					<div className="flex min-w-0 items-center gap-2">
						{job.isPromoted && (
							<span className="inline-flex items-center rounded-full bg-primary px-2.5 py-1 text-xs font-semibold uppercase tracking-widest text-primary-foreground">
								{t?.featured || "Featured"}
							</span>
						)}
					</div>
					<span className="shrink-0 text-xs font-medium text-muted-foreground">
						{job.postedAt ? (
							<RelativeTime
								date={job.postedAt}
								locale={locale}
								className={ROW_DATE_CLASS}
							/>
						) : (
							"?"
						)}
						{sourceLbl && (
							<span>
								{" "}
								· {t?.sourceVia || "via"} {sourceLbl}
							</span>
						)}
					</span>
				</div>

				<div>
					<div className="flex items-start gap-2.5">
						<CompanyLogo
							company={job.company}
							domain={job.companyDomain ?? null}
							logoR2Key={job.companyLogoR2Key ?? null}
							logoConfidence={job.companyLogoConfidence ?? null}
							size={32}
							className="mt-0.5"
						/>
						{/* Was `text-[19px] font-[700] tracking-[0.231px]` — a Figma
						    export, not a system value, and one of ten distinct arbitrary
						    sizes in this file. DESIGN.md's Two Weights Rule puts the
						    hierarchy in weight, not in a 1px size difference. */}
						<h3 className="text-pretty break-words text-lg font-semibold leading-snug tracking-tight text-foreground transition-colors group-hover:text-primary">
							{job.title}
						</h3>
					</div>

					{mobileMetaLine && (
						<p className="mt-1 truncate text-xs text-muted-foreground">
							{mobileMetaLine}
						</p>
					)}

					{isGated ? (
						<p className="mt-1.5 inline-block rounded-xl bg-muted/60 py-1.5 text-sm font-medium text-muted-foreground blur-[6px]">
							{t?.hiddenDescription ||
								"Job description hidden for this account"}
						</p>
					) : (
						<p
							className="mt-1.5 line-clamp-2 text-sm leading-[1.45] text-muted-foreground"
							data-testid="job-blurb"
							data-blurb-source={blurbSource}
						>
							{job.hook ||
								job.summary?.text ||
								t?.remoteRoleAvailable ||
								"Remote role available for this position"}
						</p>
					)}

					<div className="mt-1.5 flex flex-wrap gap-1.5">
						{mobileChipKeys.map((key) => {
							switch (key) {
								case "pay":
									return (
										<span
											key={key}
											// Stable handle for the gating e2e specs: a visible
											// `pay-badge` means THIS row is showing a readable,
											// employer-stated figure. Gated rows must never render
											// one (tests/e2e/rich-data-protection.spec.ts), and it
											// is how tests/e2e/job-access-gating.spec.ts finds a
											// row that actually has a salary to redact instead of
											// betting that a fixed feed position has one.
											data-testid="pay-badge"
											className="inline-flex items-center rounded-md bg-primary/10 px-2 py-0.5 text-xs font-semibold tracking-tight text-primary"
										>
											{payRange}
										</span>
									);
								// Research-grounded estimate — visually DISTINCT from the
								// confirmed pay chip (muted, dashed, "Est." prefix) so it never
								// reads as an employer-stated figure. The provenance note used
								// to live ONLY in `title=`, which never renders on touch: the
								// audience this product is built for could never read it. It
								// is now the chip's accessible name as well.
								case "pay-est":
									return (
										<span
											key={key}
											data-testid="pay-estimate-badge"
											className="inline-flex items-center rounded-md border border-dashed border-muted-foreground/40 bg-muted/40 px-2 py-0.5 text-xs font-semibold tracking-tight text-muted-foreground"
										>
											{estPayLabel}
											<span className="sr-only">
												{` — ${
													t?.estimatedSalaryNote ||
													"Based on similar roles — not stated by the employer"
												}`}
											</span>
										</span>
									);
								case "pay-gated":
									return (
										<span
											key={key}
											data-testid="gated-salary-placeholder"
											className="inline-flex items-center rounded-md bg-muted/60 px-2 py-0.5 text-xs font-semibold tracking-tight text-muted-foreground/40 blur-[4px]"
										>
											$XX,XXX
										</span>
									);
								// The Land Signal pill ("PAS BANGET"/"COCOK"/…) reads as a
								// personal fit verdict, so it is gated on the row actually
								// having been ranked against this viewer's profile.
								case "land-signal":
									return job.landSignal ? (
										<LandSignalPill
											key={key}
											signal={job.landSignal}
											tone={isRowV2 ? "one-voice" : "tier"}
											label={
												t?.[TIER_LABEL_KEYS[job.landSignal.tier]] ||
												job.landSignal.tier
											}
											ariaLabelTemplate={t?.landSignalAriaLabel}
											variant="compact"
										/>
									) : null;
								// 1-line "why this matches you" — the card-clickability lever
								// from the D-3 audit (CTR 1.9% is the bottleneck).
								case "match-reason":
									return (
										<span
											key={key}
											className="inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary"
										>
											{matchReason}
										</span>
									);
								case "skills-match":
									return (
										<span
											key={key}
											data-testid="skills-match-chip"
											className="inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary"
										>
											{skillsMatchLabel}
										</span>
									);
								// "Closes in Xd" — absent unless expiresAt is set AND future.
								case "urgency":
									return (
										<span
											key={key}
											data-testid="urgency-chip"
											className={`inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-xs font-semibold ${urgencyClassName}`}
										>
											<Clock aria-hidden="true" className="size-3" />
											{/* Time-dependent text: see URGENCY_HYDRATION note above. */}
											<span suppressHydrationWarning>{urgencyLabel}</span>
										</span>
									);
								// Hidden-gem / low-competition badge — a job-level attribute,
								// not a user-fit one, so it stays visible to cold-start
								// visitors too. No ARIA role at all: it used to carry
								// `role="img"` + `aria-label`, which inserted a spurious
								// graphic into the a11y tree on every hidden-gem row. The
								// visible text is already the whole message.
								case "competition":
									return (
										<span
											key={key}
											data-testid="hidden-gem-pill"
											className={`inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-xs font-semibold uppercase tracking-wide ${competitionClassName}`}
										>
											<span>{competitionBadgeLabel}</span>
										</span>
									);
								case "ai-apply":
									return (
										<AiApplyBadge
											key={key}
											ats={job.atsSupported ? "supported" : null}
											surface="feed_card"
											jobId={job.id}
											locale={locale ?? "id"}
										/>
									);
								default:
									return null;
							}
						})}
					</div>

					{/* Apply-by-email handoff CTA					    only. `relative z-10` matches SaveJobButton/apply link below: the
					    whole-card overlay Link at the end of this article is `absolute`,
					    which paints above any un-positioned static content. */}
					{showEmailHandoffCta && (
						<div className="relative z-10 mt-2">
							<ApplyEmailHandoff
								jobId={job.id}
								locale={safeLocale}
								label={emailHandoffLabel}
								slate={emailHandoffSlate}
							/>
						</div>
					)}

					{/* `relative z-10` on SaveJobButton below is REQUIRED — the whole-card
					    overlay Link at the end of this article is `absolute inset-0`, which
					    paints above any un-positioned static content (see the ApplyEmailHandoff
					    comment above). Without it every click here is swallowed by the overlay
					    instead of toggling save — confirmed root cause of job_saved/first_save
					    going silent for 8+ weeks (telemetry-gap-remediation, 2026-07-21/22). */}
					<div className="mt-2 flex items-center justify-end gap-2">
						{isGeoRestricted && !isGated && (
							<button
								type="button"
								data-testid="geo-restricted-report"
								disabled={geoReported}
								onClick={() => {
									setGeoReported(true);
									posthog?.capture("restricted_job_reported", {
										job_id: job.id,
										slot_index: index,
										origin_surface: "feed",
										reason: "cant_apply_from_indonesia",
									});
								}}
								className="relative z-10 min-h-11 px-1 text-xs text-muted-foreground underline-offset-4 hover:underline disabled:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
							>
								{geoReported
									? locale === "en"
										? "Thanks, noted"
										: "Terima kasih, dicatat"
									: locale === "en"
										? "Can't apply from Indonesia?"
										: "Tidak bisa melamar dari Indonesia?"}
							</button>
						)}
						{!isGated && (
							<SaveJobButton
								jobId={job.id}
								initialIsSaved={isSaved}
								isAuthenticated={isAuthenticated}
								canSaveJobs={canSaveJobs ?? false}
								locale={locale}
								t={t}
								categorySlugs={job.categorySlugs}
								className="relative z-10 p-3.5"
							/>
						)}
						{isGated ? (
							<Link
								href={gatedHref}
								prefetch={false}
								onClick={handleGatedClick}
								aria-label={gatedAriaLabel}
								className="relative z-10 inline-flex min-h-11 items-center justify-end gap-1.5 px-1 py-2 text-sm font-semibold text-primary transition-colors hover:text-primary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
							>
								{lockMessage} →
							</Link>
						) : (
							<Link
								href={outboundUrl}
								prefetch={false}
								target="_blank"
								rel="nofollow"
								onClick={() => {
									trackJobClick(job.id, {
										origin_surface: "feed",
										job_title: job.title,
										...categoryMeta,
										...relevanceMeta,
										...searchMeta,
										...badgeMeta,
										...rankContext,
									});
									trackApplyClick(job.id, applyPayloadBase, {
										...categoryMeta,
										...relevanceMeta,
										...searchMeta,
									});
									if (showAhaSignupAsk) dispatchFeedApplyClicked(job.id);
								}}
								className="relative z-10 inline-flex min-h-11 items-center justify-end gap-1.5 px-1 py-2 text-sm font-semibold text-primary transition-colors hover:text-primary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
							>
								{t?.apply || "Apply"} ↗
							</Link>
						)}
					</div>
				</div>
			</div>

			<div className="hidden gap-3 sm:flex sm:gap-4">
				<div className="mt-0.5 hidden w-6 shrink-0 text-right font-medium tabular-nums text-muted-foreground sm:block">
					{index}.
				</div>

				<CompanyLogo
					company={job.company}
					domain={job.companyDomain ?? null}
					logoR2Key={job.companyLogoR2Key ?? null}
					logoConfidence={job.companyLogoConfidence ?? null}
					size={40}
					className="mt-0.5"
				/>

				<div className="flex min-w-0 flex-1 flex-col">
					<div className="mb-1 flex items-start gap-2">
						{job.isPromoted && (
							<span className="mt-1.5 inline-flex shrink-0 items-center rounded-md bg-primary/10 px-1.5 py-0.5 text-xs font-semibold uppercase tracking-widest text-primary">
								{t?.featured || "Featured"}
							</span>
						)}
						{/* This IS an <h3>, matching the `sm:hidden` mobile layout above.
						    Task 4.4 (ux-seeker-journey-remediation heading census)
						    demoted it to a <div> to kill an apparent duplicate-heading
						    finding, but that census counted `<h3>` occurrences in the
						    SOURCE, and the duplicate it saw never existed in the
						    rendered accessibility tree: the two layouts are toggled by
						    `sm:hidden` / `hidden sm:flex`, and a `display: none`
						    subtree is not exposed to assistive tech at all. Exactly one
						    heading per job was ever announced.

						    What the demotion did cause was real: on a desktop viewport
						    the only surviving <h3> sat inside the hidden mobile block,
						    so the feed announced NO headings — a screen-reader user lost
						    heading navigation over the entire job list, and
						    `getByRole("heading")` stopped matching rows (which is how
						    tests/e2e/rich-data-protection.spec.ts and
						    job-access-gating.spec.ts went red → issue #3418). Keep both
						    layouts on <h3>; the visibility toggle is what guarantees
						    one. */}
						<h3 className="line-clamp-2 text-pretty text-xl font-semibold leading-snug tracking-tight text-foreground transition-colors group-hover:text-primary/90">
							{job.title}
						</h3>
					</div>

					{isGated ? (
						<p className="mb-3 inline-block self-start rounded-md bg-muted/50 py-1 text-sm font-medium text-muted-foreground blur-[6px]">
							{t?.hiddenDescription ||
								"Job description hidden for this account"}
						</p>
					) : (
						<p
							className="mb-3 inline-block self-start rounded-md bg-muted/50 py-1 text-sm font-medium text-muted-foreground"
							data-testid="job-blurb"
							data-blurb-source={blurbSource}
						>
							{job.hook ||
								job.summary?.text ||
								t?.remoteRoleAvailable ||
								"Remote role available for this position"}
						</p>
					)}

					<div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 text-xs text-muted-foreground">
						{/* 1-line "why this matches you" — desktop parity with the mobile
						    layout above. See matchReason declaration for the honesty gate. */}
						{showsDesktopChip("match-reason") && (
							<span className="inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
								{matchReason}
							</span>
						)}
						{/* Skill-match chip (T10) — desktop parity with the mobile layout
						    above. Same personalized-only gate as matchReason. */}
						{showsDesktopChip("skills-match") && (
							<span
								data-testid="skills-match-chip"
								className="inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary"
							>
								{skillsMatchLabel}
							</span>
						)}
						{/* Fit pill only on genuinely personalized rows — same guard as
						    the mobile layout above. A cold-start / logged-out visitor
						    gave no profile data, so a "Strong fit" verdict overstates a
						    generic job-quality score. (This desktop site previously had
						    no guard at all — the bug that shipped the pill to anon users.) */}
						{showsDesktopChip("land-signal") && job.landSignal && (
							<LandSignalPill
								signal={job.landSignal}
								tone={isRowV2 ? "one-voice" : "tier"}
								label={
									t?.[TIER_LABEL_KEYS[job.landSignal.tier]] ||
									job.landSignal.tier
								}
								ariaLabelTemplate={t?.landSignalAriaLabel}
							/>
						)}
						{/* Hidden-gem / low-competition badge — renders the actual numeric
						    applyClicks count (T9) when known, falling back to the
						    qualitative text otherwise. See the mobile layout comment above
						    for the full rationale. */}
						{showsDesktopChip("competition") && competitionBadgeLabel && (
							<span
								role="img"
								data-testid="hidden-gem-pill"
								aria-label={competitionBadgeLabel}
								className={`inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-xs font-semibold uppercase tracking-wide ${competitionClassName}`}
							>
								<span aria-hidden="true">◈</span>
								<span>{competitionBadgeLabel}</span>
							</span>
						)}
						{showsDesktopChip("ai-apply") ? (
							<AiApplyBadge
								ats={job.atsSupported ? "supported" : null}
								surface="feed_card"
								jobId={job.id}
								locale={locale ?? "id"}
							/>
						) : null}
						{job.company && (
							<span className="font-medium text-foreground/80">
								{job.company}
							</span>
						)}

						{(job.remoteType || job.location) && (
							<>
								<span>·</span>
								<span>{locationDisplay.text}</span>
							</>
						)}

						{hqFlag && (
							<>
								<span>·</span>
								{/* role="img" + aria-label so a screen reader announces the
								    country NAME, never the raw regional-indicator pair.
								    Windows has no flag glyphs and renders the letters
								    instead ("US") — a fine fallback, and the reason the
								    name is carried separately rather than inferred from
								    the glyph. */}
								<span role="img" aria-label={hqFlag.label} title={hqFlag.label}>
									{hqFlag.glyph}
								</span>
							</>
						)}

						{job.jobType && (
							<>
								<span>·</span>
								<span className="capitalize">
									{job.jobType.replace("-", " ")}
								</span>
							</>
						)}

						{/* Experience level (H2, job-ux-spec: the #1 fact a seeker
						    previously had to open the detail page to learn). */}
						{seniorityLbl && (
							<>
								<span>·</span>
								<span>{seniorityLbl}</span>
							</>
						)}

						{/* Fit signal = the EXCEPTION only. We never badge "Indonesia-
						    friendly" (that's the board's baseline promise, not a row
						    attribute — see src/lib/jobs/fit-blocker.ts). We show a chip
						    ONLY when the LLM fit_reason names a concrete eligibility
						    blocker (US-only / on-site / hard timezone). Rare + specific
						    + protective. Full reason in the title for desktop hover. */}
						{fitBlocker && (
							<>
								<span>·</span>
								<span
									title={job.fitReason ?? undefined}
									className="inline-flex items-center gap-1 rounded-md bg-amber-100 px-1.5 py-0.5 font-medium text-amber-800 dark:bg-amber-950/40 dark:text-amber-300"
								>
									<TriangleAlert
										className="size-3 shrink-0"
										strokeWidth={1.75}
										aria-hidden="true"
									/>
									{t?.[FIT_BLOCKER_LABEL_KEY[fitBlocker]] || "Cek kelayakan"}
								</span>
							</>
						)}

						{isGeoRestricted && (
							<>
								<span>·</span>
								<span
									data-testid="geo-restricted-chip"
									className="inline-flex items-center gap-1 rounded-md bg-amber-100 px-1.5 py-0.5 font-medium text-amber-800 dark:bg-amber-950/40 dark:text-amber-300"
								>
									<Globe
										className="size-3 shrink-0"
										strokeWidth={1.75}
										aria-hidden="true"
									/>
									{locale === "en"
										? "Region may be restricted"
										: "Mungkin terbatas wilayah"}
								</span>
							</>
						)}

						{/* "Closes in Xd" urgency chip — absent unless expiresAt is set
						    AND in the future (getDaysUntilExpiry returns null otherwise). */}
						{showsDesktopChip("urgency") && (
							<>
								<span>·</span>
								<span
									data-testid="urgency-chip"
									className={`inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 font-medium ${urgencyClassName}`}
									// Time-dependent text: see URGENCY_HYDRATION note above.
									suppressHydrationWarning
								>
									⏳ {urgencyLabel}
								</span>
							</>
						)}

						{payRange && !isGated && (
							<>
								<span>·</span>
								<span
									// Desktop twin of the mobile `pay-badge` chip above — same
									// contract for the gating e2e specs.
									data-testid="pay-badge"
									className="inline-flex rounded-md bg-primary/10 px-1.5 py-0.5 font-semibold tracking-tight text-primary"
								>
									{payRange}
								</span>
							</>
						)}
						{/* Research-grounded estimate — visually DISTINCT from the confirmed
						    pay chip above (muted, dashed border, "Est." prefix). Desktop
						    parity with the mobile layout above. */}
						{estPayLabel && !isGated && (
							<>
								<span>·</span>
								<span
									data-testid="pay-estimate-badge"
									title={
										t?.estimatedSalaryNote ||
										"Based on similar roles — not stated by the employer"
									}
									className="inline-flex rounded-md border border-dashed border-muted-foreground/40 bg-muted/40 px-1.5 py-0.5 font-semibold tracking-tight text-muted-foreground"
								>
									{estPayLabel}
								</span>
							</>
						)}
						{(payRange || estPayRange || job.hasGatedSalary) && isGated && (
							<>
								<span>·</span>
								<span
									data-testid="gated-salary-placeholder"
									className="inline-flex rounded-sm bg-muted px-1.5 py-0.5 font-medium text-muted-foreground/40 blur-[4px]"
								>
									$XX,XXX
								</span>
							</>
						)}
					</div>

					{/* Apply-by-email handoff CTA					    only. `relative z-10` matches SaveJobButton/apply link in the
					    right-side column: the whole-card overlay Link at the end of this
					    article is `absolute`, which paints above any un-positioned
					    static content. Sits in the content column (not the shrink-0
					    action column) so the button gets real width instead of being
					    squeezed to icon-size. */}
					{showEmailHandoffCta && (
						<div className="relative z-10 mt-2 max-w-sm">
							<ApplyEmailHandoff
								jobId={job.id}
								locale={safeLocale}
								label={emailHandoffLabel}
								slate={emailHandoffSlate}
							/>
						</div>
					)}
				</div>

				<div className="flex shrink-0 flex-col items-end justify-between gap-3 py-0.5">
					{/* `relative z-10` on SaveJobButton below is REQUIRED — same whole-card
					    overlay-Link stacking issue as the mobile branch above. */}
					{!isGated && (
						<SaveJobButton
							jobId={job.id}
							initialIsSaved={isSaved}
							isAuthenticated={isAuthenticated}
							canSaveJobs={canSaveJobs ?? false}
							locale={locale}
							t={t}
							categorySlugs={job.categorySlugs}
							className="relative z-10 p-3.5"
						/>
					)}
					<span className="whitespace-nowrap text-xs text-muted-foreground">
						{job.postedAt ? (
							<RelativeTime
								date={job.postedAt}
								locale={locale}
								className={ROW_DATE_CLASS}
							/>
						) : (
							"?"
						)}
						{sourceLbl && (
							<span>
								{" "}
								· {t?.sourceVia || "via"} {sourceLbl}
							</span>
						)}
					</span>

					{isGated ? (
						<Link
							href={gatedHref}
							prefetch={false}
							onClick={handleGatedClick}
							aria-label={gatedAriaLabel}
							className="relative z-10 inline-flex items-center gap-1 text-sm font-semibold text-primary transition-colors hover:underline underline-offset-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
						>
							{lockMessage}{" "}
							<span
								aria-hidden="true"
								className="mb-0.5 text-base leading-none"
							>
								›
							</span>
						</Link>
					) : (
						<a
							href={outboundUrl}
							target="_blank"
							// `noopener` only — NOT `noreferrer`. This is the outbound
							// apply link: several sources (Careerjet's declared-IP relay
							// among them) require a real Referer, so stripping it would
							// break attribution on the click that matters most.
							rel="nofollow noopener"
							onClick={() => {
								trackJobClick(job.id, {
									origin_surface: "feed",
									job_title: job.title,
									...categoryMeta,
									...relevanceMeta,
									...searchMeta,
									...badgeMeta,
									...rankContext,
								});
								trackApplyClick(job.id, applyPayloadBase, {
									...categoryMeta,
									...relevanceMeta,
									...searchMeta,
								});
								if (showAhaSignupAsk) dispatchFeedApplyClicked(job.id);
							}}
							className="relative z-10 inline-flex min-h-11 items-center gap-1 text-sm font-medium text-primary underline-offset-4 transition-colors hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
						>
							{t?.apply || "Apply"}{" "}
							<span
								aria-hidden="true"
								className="mb-0.5 text-base leading-none"
							>
								›
							</span>
						</a>
					)}
				</div>
			</div>

			{showAhaSignupAsk ? (
				<FeedAhaSignupAsk
					jobId={job.id}
					detailPath={detailUrl}
					locale={safeLocale}
				/>
			) : null}

			{!isGated && (
				<Link
					href={detailUrl}
					prefetch={false}
					data-testid="job-card-detail-link"
					className="absolute inset-0 z-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
					// Was hardcoded English. This label is announced on every one of
					// the ~20 rows on a page, so on /id a screen-reader user heard an
					// English sentence twenty times per feed.
					aria-label={(
						t?.viewDetailsAria || "View details for {title}"
					).replace("{title}", job.title)}
					onClick={() => {
						trackJobClick(job.id, {
							origin_surface: "feed_card",
							job_title: job.title,
							...categoryMeta,
							...relevanceMeta,
							...searchMeta,
							...badgeMeta,
							...rankContext,
						});
					}}
				>
					{/* Tints the row while this navigation is in flight — the tap used
					    to produce no visible change at all. See card-nav-pending.tsx
					    for why it reads `useLinkStatus` instead of a `loading.tsx` or a
					    router-driven transition. */}
					<CardNavPending />
				</Link>
			)}

			{isGated && (
				<Link
					href={gatedHref}
					prefetch={false}
					className="absolute inset-0 z-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
					aria-label={gatedAriaLabel}
					onClick={handleGatedClick}
				/>
			)}
		</article>
	);
}
